import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, GeoJSON, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTenantSafe } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { canStaffSection } from '../utils/staffAccess';
import {
  getPolygons, createPolygon, updatePolygon, approvePolygon, archivePolygon, deletePolygon,
  getAssignments, createAssignment, updateAssignment, approveAssignment, archiveAssignment,
  getLayerPolygons,
} from '../services/stewardshipService';
import { getLayerCatalog, searchGeographicFeatures, getAvailableGeographyLayers, searchManagedGeographyFeatures } from '../services/geographyService';
import SpeciesAutocomplete from '../components/harvesting/SpeciesAutocomplete';
import SpeciesImport from '../components/harvesting/SpeciesImport';
import StewardshipSpeciesSummary from '../components/harvesting/StewardshipSpeciesSummary';
import EvidencePanel from '../components/harvesting/EvidencePanel';
import LakeSelectionMap from '../components/harvesting/LakeSelectionMap';
import BulkLakeGuidance from '../components/harvesting/BulkLakeGuidance';
import { isLakesLayer } from '../components/harvesting/MapLayerControls';
import { scoreToLabel } from '../utils/stewardshipColors';
import { searchSpecies } from '../services/speciesService';

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page:     { display: 'grid', gridTemplateColumns: '320px 1fr', height: 'calc(100vh - 56px)', overflow: 'hidden' },
  left:     { borderRight: '1px solid #e2e8f0', overflowY: 'auto', background: '#f8fafc', display: 'flex', flexDirection: 'column' },
  right:    { overflowY: 'auto', padding: '24px 28px' },
  badge:    (s) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, marginLeft: 6,
    background: s === 'approved' ? '#dcfce7' : s === 'draft' ? '#fef9c3' : '#f1f5f9',
    color:      s === 'approved' ? '#166534' : s === 'draft' ? '#854d0e' : '#64748b',
  }),
  scoreChip: (score) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700, marginLeft: 6,
    background: score > 0 ? '#dcfce7' : score < 0 ? '#fee2e2' : '#f1f5f9',
    color:      score > 0 ? '#166534' : score < 0 ? '#dc2626' : '#64748b',
  }),
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%', marginBottom: 8 },
  btn:   (bg = '#16a34a', fg = '#fff') => ({
    padding: '7px 16px', background: bg, color: fg, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  }),
  tab: (active) => ({
    flex: 1, padding: '9px 0', background: active ? '#fff' : '#f1f5f9', border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400, color: active ? '#1e293b' : '#64748b',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  }),
};

// ─── Inline draw map ──────────────────────────────────────────────────────────
function PolygonDrawMap({ vertices, onAdd, center = [51.0, -91.0] }) {
  const ClickHandler = () => { useMapEvents({ click: (e) => onAdd([e.latlng.lat, e.latlng.lng]) }); return null; };
  return (
    <MapContainer center={center} zoom={7}
      style={{ height: 280, width: '100%', cursor: 'crosshair', borderRadius: 6, border: '1px solid #bfdbfe' }}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <ClickHandler />
      {vertices.length >= 3 && <Polygon positions={vertices} pathOptions={{ color: '#2563eb', fillColor: '#bfdbfe', fillOpacity: 0.35 }} />}
    </MapContainer>
  );
}

// ─── Left panel ───────────────────────────────────────────────────────────────
function searchLakeFeatures(q, layer, tenantId, limit, bbox, managed = false) {
  return managed || isLakesLayer({ layerId: layer })
    ? searchManagedGeographyFeatures(layer, { q, tenantId, module: 'species_stewardship', limit, ...(bbox ? { bbox } : {}) })
    : searchGeographicFeatures(q, { layer, limit });
}

export function LakesPanel({
  tenantId, layers = [], selectedId, onSelect, onCreate, onLayerChange,
  bbox, localOnly = false, onLocalOnlyChange, selectedLakeIds = [], onToggleLake,
  refreshKey = 0, disabled = false,
}) {
  const [activeLayer, setLayer]   = useState('ontario_lakes');
  const [lakeSearch, setSearch]   = useState('');
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [recordMap, setRecordMap] = useState({}); // sourceFeatureId → { polygonId, status, assignments }
  const [filter, setFilter]       = useState('all'); // all | tracked | untracked
  const [error, setError]         = useState('');
  const isManaged = layers.some((layer) => layer.id === activeLayer && layer.managed) || isLakesLayer({ layerId: activeLayer });
  const noun = isLakesLayer({ layerId: activeLayer }) || !isManaged ? 'lakes' : 'areas';
  const localSearch = isManaged && localOnly && !lakeSearch.trim();
  const searchBbox = localSearch ? bbox : null;

  useEffect(() => {
    setLayer(layers[0]?.id || 'ontario_lakes');
    onLayerChange?.(layers[0]?.id || 'ontario_lakes');
    setResults([]);
  }, [layers, onLayerChange]);

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setRecordMap({});
    getLayerPolygons(tenantId, activeLayer)
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data || []).forEach((rec) => { map[String(rec.sourceFeatureId)] = rec; });
        setRecordMap(map);
      })
      .catch((err) => { if (alive) setError(err.response?.data?.message || 'Could not load tracked source features.'); });
    return () => { alive = false; };
  }, [tenantId, activeLayer, selectedId, refreshKey]);

  useEffect(() => {
    if (!layers.length) return undefined;
    let alive = true;
    setLoading(true);
    setResults([]);
    setError('');
    if (localSearch && !searchBbox) {
      setLoading(false);
      return () => { alive = false; };
    }
    const t = setTimeout(async () => {
      try {
        const res = await searchLakeFeatures(lakeSearch, activeLayer, tenantId, 60, searchBbox, isManaged);
        if (alive) setResults(res.data || []);
      } catch (err) {
        if (alive) setError(err.response?.data?.message || 'Could not load source features.');
      } finally {
        if (alive) setLoading(false);
      }
    }, 260);
    return () => { alive = false; clearTimeout(t); };
  }, [lakeSearch, activeLayer, tenantId, layers, localSearch, searchBbox, isManaged]);

  const filtered = results.filter((lake) => {
    const hasRecord = !!recordMap[String(lake.id)];
    if (filter === 'tracked')   return hasRecord;
    if (filter === 'untracked') return !hasRecord;
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Layer selector */}
      {layers.length > 1 && (
        <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
          <select
            value={activeLayer}
            disabled={disabled}
            onChange={(e) => { setLayer(e.target.value); setSearch(''); onLayerChange?.(e.target.value); }}
            style={{ ...S.input, marginBottom: 0, fontSize: 12 }}
          >
            {layers.map((l) => (
              <option key={l.id} value={l.id}>{l.label}</option>
            ))}
          </select>
        </div>
      )}
      <p style={{ padding: '0 10px', fontSize: 12, color: '#64748b' }}>
        {isManaged
          ? `${layers.find((layer) => layer.id === activeLayer)?.label || activeLayer}: names and boundaries from the active geography version.`
          : 'Legacy names-only catalogue. Upload, activate and enable lakes_and_aoc for Species / stewardship to use lake boundaries.'}
      </p>
      {error && <p role="alert" style={{ padding: '0 10px', color: '#dc2626' }}>{error}</p>}

      {/* Search + filter */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
        {onLocalOnlyChange && isManaged && (
          <label style={{ display: 'block', marginBottom: 8 }}>
            <input type="checkbox" checked={localOnly} onChange={(e) => onLocalOnlyChange(e.target.checked)} disabled={disabled} />
            Only {noun} in map view (name search looks everywhere)
          </label>
        )}
        <input
          value={lakeSearch}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${noun}…`}
          disabled={disabled}
          style={{ ...S.input, marginBottom: 6 }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {[['all','All'],['tracked','Tracked'],['untracked','No record']].map(([v,l]) => (
            <button
              key={v}
              type="button"
              onClick={() => setFilter(v)}
              style={{
                flex: 1, padding: '4px 0', fontSize: 11, borderRadius: 4, border: 'none', cursor: 'pointer',
                background: filter === v ? '#2563eb' : '#e2e8f0',
                color: filter === v ? '#fff' : '#475569',
                fontWeight: filter === v ? 700 : 400,
              }}
            >{l}</button>
          ))}
        </div>
      </div>

      {/* Lake list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && <p style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>Loading…</p>}
        {!loading && filtered.length === 0 && (
          <p style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>
            {lakeSearch ? `No ${noun} match your search.` : localOnly && !bbox && isManaged ? `Zoom in to see local ${noun}, or search by name.` : `No ${noun} found in this area.`}
          </p>
        )}
        {!loading && results.length === 60 && <p style={{ padding: 10, fontSize: 12 }}>Showing the first 60 matching {noun}. Zoom closer or refine the name search.</p>}
        {filtered.map((lake) => {
          const rec      = recordMap[String(lake.id)];
          const isActive = rec && selectedId === String(rec.polygonId);
          return (
            <div
              key={lake.id}
              onClick={() => { if (!disabled) { if (rec) onSelect(String(rec.polygonId)); else onCreate(lake, activeLayer); } }}
              style={{
                padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                background: isActive ? '#eff6ff' : 'transparent',
                borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontWeight: rec ? 600 : 400, fontSize: 13, color: '#1e293b' }}>
                {onToggleLake && isManaged && <input type="checkbox"
                  aria-label={`Select ${lake.name}`} disabled={disabled}
                  checked={selectedLakeIds.includes(String(lake.id))}
                  onClick={(event) => event.stopPropagation()} onChange={() => onToggleLake(lake)} />}
                {lake.name}
              </div>
              <div style={{ marginTop: 3, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{lake.type}</span>
                {rec ? (
                  <>
                    <span style={S.badge(rec.status)}>{rec.status}</span>
                    {rec.assignments.total > 0 && (
                      <span style={{ fontSize: 11, color: '#64748b' }}>
                        {rec.assignments.total} species
                        {rec.assignments.approved > 0 && <span style={{ color: '#16a34a' }}> · {rec.assignments.approved} live</span>}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: '#2563eb' }}>+ Track this {noun === 'lakes' ? 'lake' : 'area'}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AreasPanel({ polygons, loading, selectedId, onSelect, search, onSearch, onNew, page, pages, onPage }) {
  const filtered = polygons;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search areas…" style={{ ...S.input, marginBottom: 6 }} />
        <button style={{ ...S.btn(), width: '100%', justifyContent: 'center' }} onClick={onNew}>+ New Custom Area</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && <p style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>Loading…</p>}
        {!loading && filtered.length === 0 && <p style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>No matching guidance records.</p>}
        {filtered.map((p) => (
          <div
            key={p._id}
            onClick={() => onSelect(p._id)}
            style={{
              padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
              background: selectedId === p._id ? '#eff6ff' : 'transparent',
              borderLeft: selectedId === p._id ? '3px solid #2563eb' : '3px solid transparent',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
            <div style={{ marginTop: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={S.badge(p.status)}>{p.status}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{p.geometryType?.replace(/_/g,' ')}</span>
              {p.assignmentCount > 0 && <span style={{ fontSize: 11, color: '#64748b' }}>{p.assignmentCount} species</span>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 10 }}>
        <button type="button" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
        <span> Page {page} of {Math.max(1, pages)} </span>
        <button type="button" disabled={loading || page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function StewardshipStaffPage() {
  // Under /t/:tenantSlug this reads the tenant being previewed; on the bare
  // /stewardship route (no TenantProvider) it falls back to the viewer's own tenant.
  const tenantCtx = useTenantSafe();
  const { activeTenant, user, access } = useAuth();
  const viewTenant = tenantCtx?.viewTenant || activeTenant;
  const tenantId = viewTenant?._id;
  const canBulkApprove = canStaffSection(user, tenantId, 'stewardship');
  const canManageTerritory = ['cip_admin', 'cip_staff'].includes(user?.globalRole)
    || access?.communityRoles?.[tenantId] === 'community_admin';

  const [leftTab, setLeftTab]           = useState('lakes'); // lakes | areas
  const [polygons, setPolygons]         = useState([]);
  const [selectedPolygon, setSelected]  = useState(null);
  const canManageBoundary = selectedPolygon?.geometryType !== 'traditional_land' || canManageTerritory;
  const [assignments, setAssignments]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [msg, setMsg]                   = useState('');
  const [areaSearch, setAreaSearch]     = useState('');
  const [areaPage, setAreaPage] = useState(1);
  const [areaPages, setAreaPages] = useState(1);
  const areaRequest = useRef(0);

  // Create/edit polygon form
  const [creating, setCreating]         = useState(false);
  const [newPoly, setNewPoly]           = useState({ name: '', description: '', geometryType: 'other' });
  const [polyVertices, setPolyVertices] = useState([]);
  const [editGeomMode, setEditGeomMode] = useState(false);
  const [editGeomVerts, setEditGeomV]   = useState([]);
  const [lakeQuery, setLakeQuery]       = useState('');
  const [lakeSugg, setLakeSugg]         = useState([]);
  const [lakeLoading, setLakeLoading]   = useState(false);
  const [lakeLayers, setLakeLayers]     = useState([]);
  const [managedLakeLayers, setManagedLakeLayers] = useState([]);
  const [activeLakeSource, setActiveLakeSource] = useState('');
  const [lakeViewport, setLakeViewport] = useState(null);
  const [localLakeList, setLocalLakeList] = useState(true);
  const [selectedLakes, setSelectedLakes] = useState([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [lakeRefresh, setLakeRefresh] = useState(0);
  const lakeScope = `${tenantId}:${activeLakeSource}`;
  const currentLakeScope = useRef(lakeScope);
  currentLakeScope.current = lakeScope;
  const managedLakeLayer = managedLakeLayers.find((layer) => layer.layerId === activeLakeSource);
  const changeLakeSource = useCallback((source) => {
    setActiveLakeSource(source);
    setLakeViewport(null);
    setAreaPage(1);
    setSelectedLakes([]);
    setSelected(null);
  }, []);
  const toggleLake = (lake) => {
    const id = String(lake.id);
    if (selectedLakes.some((entry) => String(entry.id) === id)) setSelectedLakes((current) => current.filter((entry) => String(entry.id) !== id));
    else if (selectedLakes.length >= 50) setMsg('Select at most 50 source features at a time.');
    else setSelectedLakes((current) => [...current, { ...lake, id }]);
  };
  const addLakes = (lakes) => {
    const combined = new Map([...selectedLakes, ...lakes].map((lake) => [String(lake.id), { ...lake, id: String(lake.id) }]));
    if (combined.size > 50) { setMsg('That would select more than 50 source features. Clear some or select a smaller area.'); return; }
    setSelectedLakes([...combined.values()]);
  };

  useEffect(() => {
    if (!tenantId) return undefined;
    let alive = true;
    setLakeLayers([]);
    setManagedLakeLayers([]);
    setSelected(null);
    setAreaPage(1);
    setSelectedLakes([]);
    setLakeViewport(null);
    Promise.all([
      getLayerCatalog(),
      getAvailableGeographyLayers({ tenantId, module: 'species_stewardship' }),
    ]).then(([legacy, managed]) => {
      if (!alive) return;
      const lakes = (managed.data || [])
        .sort((a, b) => Number(b.layerId === 'lakes') - Number(a.layerId === 'lakes'));
      setManagedLakeLayers(lakes);
      setLakeLayers([
        ...lakes.map((layer) => ({ id: layer.layerId, label: layer.displayName, managed: true })),
        ...(legacy.data || []).map((layer) => ({ ...layer, label: `${layer.label} (legacy names only)` })),
      ]);
    }).catch((err) => {
      if (alive) setMsg(err.response?.data?.message || 'Could not load lake sources. Reload to try again.');
    });
    return () => { alive = false; };
  }, [tenantId]);

  // Assign species
  const [assignForm, setAssignForm]     = useState({ speciesId: '', speciesName: '', score: 0, summary: '', reason: '', season: '' });
  const [editAssignId, setEditAssignId] = useState(null);
  const [showEvidence, setShowEvidence] = useState(null);
  const [savingAssign, setSavingAssign] = useState(false);

  const loadAreas = useCallback(async () => {
    if (!tenantId) return;
    const request = ++areaRequest.current;
    setLoading(true);
    try {
      const r = await getPolygons(tenantId, { limit: 60, page: areaPage, q: areaSearch, summary: true });
      if (request !== areaRequest.current) return;
      setPolygons(r.data.polygons || []);
      setAreaPages(r.data.pages || 1);
    } catch (error) {
      if (request === areaRequest.current) setMsg(error.response?.data?.message || 'Could not load guidance records.');
    } finally { if (request === areaRequest.current) setLoading(false); }
  }, [tenantId, areaPage, areaSearch]);

  useEffect(() => {
    const timer = setTimeout(loadAreas, 250);
    return () => { clearTimeout(timer); areaRequest.current += 1; };
  }, [loadAreas]);

  const loadAssignments = useCallback(async (polygonId) => {
    if (!polygonId) return;
    try {
      const r = await getAssignments(tenantId, { polygonId });
      setAssignments(r.data || []);
    } catch (error) { setMsg(error.response?.data?.message || 'Could not open the lake record.'); }
  }, [tenantId]);

  useEffect(() => {
    if (selectedPolygon) loadAssignments(selectedPolygon._id);
  }, [selectedPolygon, loadAssignments]);

  // Open a polygon by id (used by both tabs)
  const openPolygon = useCallback(async (id) => {
    const scope = currentLakeScope.current;
    try {
      const { getPolygon } = await import('../services/stewardshipService');
      const r = await getPolygon(tenantId, id);
      if (scope !== currentLakeScope.current) return;
      setSelected(r.data);
      setEditGeomMode(false);
      setEditGeomV([]);
      setAssignForm({ speciesId: '', speciesName: '', score: 0, summary: '', reason: '', season: '' });
      setEditAssignId(null);
      setShowEvidence(null);
    } catch (error) {
      if (scope === currentLakeScope.current) setMsg(error.response?.data?.message || 'Could not open the lake record.');
    }
  }, [tenantId]);

  // Create a lake record from the catalog
  const createLakeRecord = useCallback(async (lake, sourceDataset) => {
    const scope = currentLakeScope.current;
    try {
      const r = await createPolygon(tenantId, {
        name:            lake.name,
        description:     '',
        geometryType:    'lake',
        sourceDataset,
        ...(sourceDataset !== 'ontario_lakes' ? { managedLayerId: sourceDataset } : {}),
        sourceFeatureId: String(lake.id),
        geometry:        { type: 'Polygon', coordinates: [] },
      });
      if (scope !== currentLakeScope.current) return;
      setSelected(r.data);
      setMsg(`Lake record created for "${lake.name}"`);
    } catch (err) {
      if (scope === currentLakeScope.current) setMsg(err.response?.data?.message || 'Could not create lake record');
    }
  }, [tenantId]);

  const handleCreateArea = async () => {
    if (!newPoly.name.trim()) { setMsg('Name is required.'); return; }
    let geometry = { type: 'Polygon', coordinates: [] };
    if (polyVertices.length >= 3) {
      const ring = polyVertices.map(([lat, lng]) => [lng, lat]);
      ring.push(ring[0]);
      geometry = { type: 'Polygon', coordinates: [ring] };
    }
    try {
      const r = await createPolygon(tenantId, { ...newPoly, geometry });
      setPolygons((prev) => [{ ...r.data, assignmentCount: 0 }, ...prev]);
      setCreating(false);
      setNewPoly({ name: '', description: '', geometryType: 'other' });
      setPolyVertices([]);
      setMsg('Area created as draft.');
    } catch (err) { setMsg(err.response?.data?.message || 'Create failed'); }
  };

  const handleApprovePolygon = async (id) => {
    try {
      const r = await approvePolygon(tenantId, id);
      setPolygons((prev) => prev.map((p) => p._id === id ? { ...r.data, assignmentCount: p.assignmentCount } : p));
      setSelected(r.data);
      setMsg('Area approved.');
    } catch (err) { setMsg(err.response?.data?.message || 'Failed'); }
  };

  const handleDeletePolygon = async (id) => {
    if (!window.confirm('Permanently delete this area and all species assignments? Cannot be undone.')) return;
    try {
      await deletePolygon(tenantId, id);
      setPolygons((prev) => prev.filter((p) => p._id !== id));
      setSelected(null);
      setMsg('Area deleted.');
    } catch (err) { setMsg(err.response?.data?.message || 'Delete failed'); }
  };

  const handleSaveGeometry = async () => {
    if (editGeomVerts.length < 3) { setMsg('Need 3+ points.'); return; }
    const ring = editGeomVerts.map(([lat, lng]) => [lng, lat]);
    ring.push(ring[0]);
    try {
      const r = await updatePolygon(tenantId, selectedPolygon._id, { geometry: { type: 'Polygon', coordinates: [ring] } });
      setSelected(r.data);
      setPolygons((prev) => prev.map((p) => p._id === r.data._id ? { ...r.data, assignmentCount: p.assignmentCount } : p));
      setEditGeomMode(false);
      setEditGeomV([]);
      setMsg('Boundary saved.');
    } catch (err) { setMsg(err.response?.data?.message || 'Save failed'); }
  };

  const handleSaveAssign = async () => {
    let { speciesId, speciesName } = assignForm;
    if (!speciesId && speciesName.trim()) {
      try {
        const res = await searchSpecies(speciesName.trim(), { limit: 1 });
        const hit = (res.data || [])[0];
        if (hit) { speciesId = hit._id; speciesName = hit.commonName; setAssignForm((p) => ({ ...p, speciesId, speciesName })); }
      } catch (_) {}
    }
    if (!speciesId) { setMsg('Select a species.'); return; }
    setSavingAssign(true);
    setMsg('');
    try {
      if (editAssignId) {
        await updateAssignment(tenantId, editAssignId, {
          score: Number(assignForm.score), summary: assignForm.summary, reason: assignForm.reason, season: assignForm.season,
        });
      } else {
        await createAssignment(tenantId, {
          polygonId: selectedPolygon._id,
          speciesId,
          score:   Number(assignForm.score),
          summary: assignForm.summary,
          reason:  assignForm.reason,
          season:  assignForm.season,
        });
      }
      await loadAssignments(selectedPolygon._id);
      await loadAreas();
      setAssignForm({ speciesId: '', speciesName: '', score: 0, summary: '', reason: '', season: '' });
      setEditAssignId(null);
      setMsg('Assignment saved.');
    } catch (err) { setMsg(err.response?.data?.message || 'Save failed'); }
    finally { setSavingAssign(false); }
  };

  const handleApproveAssign = async (id) => {
    try { await approveAssignment(tenantId, id); await loadAssignments(selectedPolygon._id); setMsg('Assignment published.'); }
    catch (err) { setMsg(err.response?.data?.message || 'Failed'); }
  };

  const handleArchiveAssign = async (id) => {
    if (!window.confirm('Archive this assignment?')) return;
    try { await archiveAssignment(tenantId, id); await loadAssignments(selectedPolygon._id); setMsg('Assignment archived.'); }
    catch (err) { setMsg(err.response?.data?.message || 'Failed'); }
  };

  const startEdit = (a) => {
    setAssignForm({ speciesId: a.speciesId?._id || a.speciesId, speciesName: a.speciesId?.commonName || '', score: a.score, summary: a.summary, reason: a.reason, season: a.season || '' });
    setEditAssignId(a._id);
  };

  // Lake autocomplete for New Area form
  const searchLakes = useCallback(async (q) => {
    if (!q || q.length < 2) { setLakeSugg([]); return; }
    setLakeLoading(true);
    try {
      const sourceDataset = lakeLayers.find((layer) => isLakesLayer({ layerId: layer.id }) || layer.id === 'ontario_lakes')?.id;
      if (!sourceDataset) throw new Error('Lake sources are not loaded yet.');
      const res = await searchLakeFeatures(q, sourceDataset, tenantId, 10);
      setLakeSugg((res.data || []).map((lake) => ({ ...lake, sourceDataset })));
    } catch (err) {
      setLakeSugg([]);
      setMsg(err.response?.data?.message || err.message || 'Could not search lakes.');
    }
    setLakeLoading(false);
  }, [lakeLayers, tenantId]);

  useEffect(() => {
    const t = setTimeout(() => searchLakes(lakeQuery), 250);
    return () => clearTimeout(t);
  }, [lakeQuery, searchLakes]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* ── Left panel ── */}
      <div style={S.left}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <button style={S.tab(leftTab === 'lakes')} onClick={() => setLeftTab('lakes')}>Source layers</button>
          <button style={S.tab(leftTab === 'areas')} onClick={() => setLeftTab('areas')}>Guidance records</button>
        </div>

        {leftTab === 'lakes' && (
          <LakesPanel
            onLayerChange={changeLakeSource}
            bbox={lakeViewport}
            localOnly={localLakeList}
            onLocalOnlyChange={setLocalLakeList}
            selectedLakeIds={selectedLakes.map((lake) => String(lake.id))}
            onToggleLake={toggleLake}
            refreshKey={lakeRefresh}
            disabled={batchBusy}
            layers={lakeLayers}
            tenantId={tenantId}
            selectedId={selectedPolygon ? String(selectedPolygon._id) : null}
            onSelect={openPolygon}
            onCreate={createLakeRecord}
          />
        )}

        {leftTab === 'areas' && (
          <AreasPanel
            polygons={polygons}
            loading={loading}
            selectedId={selectedPolygon?._id}
            onSelect={openPolygon}
            search={areaSearch}
            onSearch={(value) => { setAreaSearch(value); setAreaPage(1); }}
            page={areaPage} pages={areaPages} onPage={setAreaPage}
            onNew={() => setCreating(true)}
          />
        )}
      </div>

      {/* ── Right panel ── */}
      <div style={S.right}>
        <h2>Stewardship workspace</h2>
        <p>For staff and stewards: monitor community harvest records, manage species-area guidance, and review boundaries before publishing advice to harvesters. Logging a harvest does not automatically create guidance.</p>
        <p><strong>Here:</strong> assign a shared species to an area and publish its score or caution. <strong>Harvest Setup:</strong> assign that species to a community logging category and configure collection methods/questions. <strong>Species catalogue:</strong> maintain names, aliases and classification once. The import button here uses that same catalogue; it does not create a separate stewardship species list.</p>
        <StewardshipSpeciesSummary key={tenantId} tenantId={tenantId} refreshKey={lakeRefresh} />
        <SpeciesImport />
        {msg && (
          <div style={{
            padding: '8px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13,
            background: /saved|created|approv|publish|deleted/.test(msg) ? '#f0fdf4' : '#fef2f2',
            color:      /saved|created|approv|publish|deleted/.test(msg) ? '#166534' : '#b91c1c',
            border: '1px solid',
            borderColor: /saved|created|approv|publish|deleted/.test(msg) ? '#86efac' : '#fca5a5',
          }}>{msg}</div>
        )}

        {/* ── New custom area form ── */}
        {creating && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>New Custom Area</h3>

            {/* Lake quick-fill */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>
                Search lakes (optional pre-fill)
              </label>
              <input
                placeholder="e.g. Cloud Lake, Balsam Lake…"
                value={lakeQuery}
                onChange={(e) => setLakeQuery(e.target.value)}
                style={{ ...S.input, marginBottom: 0 }}
              />
              {lakeSugg.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,.1)', zIndex: 200, maxHeight: 180, overflowY: 'auto' }}>
                  {lakeSugg.map((lake) => (
                    <div key={lake.id} onMouseDown={() => {
                      setNewPoly((p) => ({ ...p, name: lake.name, geometryType: 'lake', sourceDataset: lake.sourceDataset, sourceFeatureId: String(lake.id) }));
                      setLakeQuery(''); setLakeSugg([]);
                    }} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f9ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >
                      {lake.name} <span style={{ color: '#94a3b8', fontSize: 11 }}>{lake.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input placeholder="Area name *" value={newPoly.name} onChange={(e) => setNewPoly((p) => ({ ...p, name: e.target.value }))} style={S.input} />
            <input placeholder="Description" value={newPoly.description} onChange={(e) => setNewPoly((p) => ({ ...p, description: e.target.value }))} style={S.input} />
            <select value={newPoly.geometryType} onChange={(e) => setNewPoly((p) => ({ ...p, geometryType: e.target.value }))} style={{ ...S.input }}>
              {['lake','river','wetland','terrestrial_area','gathering_area','watershed','traditional_land','project_area','other']
                .filter((type) => type !== 'traditional_land' || canManageTerritory).map((t) => (
                <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
              ))}
            </select>

            <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
              <strong>Draw boundary</strong> (optional) — click to place vertices:
              {polyVertices.length > 0 && <span style={{ color: '#2563eb', marginLeft: 6 }}>{polyVertices.length} pts</span>}
            </div>
            <PolygonDrawMap vertices={polyVertices} onAdd={(pt) => setPolyVertices((p) => [...p, pt])} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 12 }}>
              {polyVertices.length > 0 && <button style={S.btn('#f3f4f6','#374151')} onClick={() => setPolyVertices((p) => p.slice(0,-1))}>Undo</button>}
              {polyVertices.length > 0 && <button style={S.btn('#fef2f2','#dc2626')} onClick={() => setPolyVertices([])}>Clear</button>}
              {polyVertices.length >= 3 && <span style={{ fontSize: 12, color: '#16a34a', alignSelf: 'center' }}>✓ Polygon ready</span>}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.btn()} onClick={handleCreateArea}>Create</button>
              <button style={S.btn('#f3f4f6','#374151')} onClick={() => { setCreating(false); setPolyVertices([]); setLakeQuery(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {leftTab === 'lakes' && managedLakeLayer && !creating && (
          <>
            <h3>{managedLakeLayer.displayName}</h3>
            <LakeSelectionMap key={`${tenantId}-${managedLakeLayer._id || managedLakeLayer.layerId}`}
              featureNoun={isLakesLayer(managedLakeLayer) ? 'lakes' : 'areas'}
              layer={managedLakeLayer} tenantId={tenantId}
              geometry={selectedPolygon?.sourceDataset === activeLakeSource ? selectedPolygon.geometry : null}
              geometryKey={`${selectedPolygon?._id}-${selectedPolygon?.updatedAt}`}
              onViewportChange={setLakeViewport} onToggleLake={toggleLake} onAddLakes={addLakes}
              selectedIds={selectedLakes.map((lake) => String(lake.id))} disabled={batchBusy} />
            {selectedLakes.length > 0 && <BulkLakeGuidance key={lakeScope} tenantId={tenantId} sourceDataset={activeLakeSource}
              featureNoun={isLakesLayer(managedLakeLayer) ? 'lakes' : 'areas'}
              lakes={selectedLakes} onClear={() => setSelectedLakes([])} onBusyChange={setBatchBusy}
              canApprove={canBulkApprove}
              onSaved={(result) => {
                if (lakeScope !== currentLakeScope.current) return;
                setMsg(`Bulk action completed for ${result.lakeCount} source features.`);
                setLakeRefresh((value) => value + 1);
                if (selectedPolygon) openPolygon(selectedPolygon._id);
              }} />}
          </>
        )}

        {/* ── No selection placeholder ── */}
        {!selectedPolygon && !creating && (
          <div style={{ color: '#64748b', textAlign: 'center', marginTop: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗺️</div>
            <p><strong>Source layers:</strong> Browse uploaded lakes, wildlife areas or grid cells. Select only the features you want to assign to a species.</p>
            <p><strong>Guidance records:</strong> Review copied boundaries, existing species assignments and custom areas. Archive obsolete guidance here.</p>
          </div>
        )}

        {/* ── Polygon detail ── */}
        {selectedPolygon && (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>{selectedPolygon.name}</h2>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={S.badge(selectedPolygon.status)}>{selectedPolygon.status}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{selectedPolygon.geometryType?.replace(/_/g,' ')}</span>
                  {selectedPolygon.sourceDataset && (
                    <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: 4 }}>
                      {selectedPolygon.sourceDataset === 'ontario_lakes' ? 'Ontario Lakes Catalog' : selectedPolygon.sourceDataset}
                    </span>
                  )}
                </div>
                {selectedPolygon.description && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{selectedPolygon.description}</p>}
                {!selectedPolygon.sourceDataset && <p style={{ fontSize: 12, color: '#64748b' }}>Custom or legacy boundary, not linked to an uploaded source. Archive obsolete assignments below to remove their published guidance.</p>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selectedPolygon.status === 'approved' && <span>Boundary confirmed</span>}
                {canManageBoundary && selectedPolygon.status === 'draft' && <button style={S.btn()} onClick={() => handleApprovePolygon(selectedPolygon._id)}>Confirm boundary</button>}
                {canManageBoundary && selectedPolygon.status !== 'archived' && (
                  <button style={S.btn('#f3f4f6','#374151')} onClick={() => archivePolygon(tenantId, selectedPolygon._id)
                    .then(() => setSelected((p) => ({ ...p, status: 'archived' })))
                    .catch((err) => setMsg(err.response?.data?.message || 'Unable to archive this boundary.'))}>Archive</button>
                )}
                {canManageBoundary && <button style={S.btn('#fef2f2','#dc2626')} onClick={() => handleDeletePolygon(selectedPolygon._id)}>🗑 Delete</button>}
                {!canManageBoundary && <span>Community admins manage traditional territory boundaries. Species guidance remains below.</span>}
              </div>
            </div>

            {/* Polygon preview / boundary */}
            {(() => {
              const geo = selectedPolygon.geometry;
              const hasGeo = geo?.type === 'MultiPolygon'
                ? geo.coordinates?.[0]?.[0]?.length >= 4
                : geo?.coordinates?.[0]?.length >= 4;
              if (editGeomMode) {
                return (
                  <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14, marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af', marginBottom: 6 }}>
                      Drawing boundary — click map to add vertices
                      {editGeomVerts.length > 0 && <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>{editGeomVerts.length} pts</span>}
                    </div>
                    <PolygonDrawMap vertices={editGeomVerts} onAdd={(pt) => setEditGeomV((p) => [...p, pt])} center={editGeomVerts[0] || [51.0,-91.0]} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      {editGeomVerts.length >= 3 && <button style={S.btn()} onClick={handleSaveGeometry}>💾 Save Boundary</button>}
                      {editGeomVerts.length > 0 && <button style={S.btn('#f3f4f6','#374151')} onClick={() => setEditGeomV((p) => p.slice(0,-1))}>Undo</button>}
                      {editGeomVerts.length > 0 && <button style={S.btn('#fef2f2','#dc2626')} onClick={() => setEditGeomV([])}>Clear</button>}
                      <button style={{ ...S.btn('#f3f4f6','#374151'), marginLeft: 'auto' }} onClick={() => { setEditGeomMode(false); setEditGeomV([]); }}>Cancel</button>
                    </div>
                  </div>
                );
              }
              if (hasGeo) {
                if (leftTab === 'lakes' && managedLakeLayer && selectedPolygon.sourceDataset === activeLakeSource) {
                  return <p style={{ fontSize: 12, color: '#64748b' }}>Selected stewardship boundary highlighted on the map above. Nearby lakes are selectable without leaving this record.</p>;
                }
                return (
                  <div style={{ marginBottom: 12 }}>
                    <MapContainer key={`${selectedPolygon._id}-${selectedPolygon.updatedAt}`} bounds={L.geoJSON(geo).getBounds()} style={{ height: selectedPolygon.geometryType === 'lake' ? 320 : 180, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <GeoJSON data={geo} style={{ color: '#2563eb', fillColor: '#bfdbfe', fillOpacity: 0.35 }} />
                    </MapContainer>
                    {geo.type === 'Polygon' && geo.coordinates.length === 1 ? (
                    <button type="button" style={{ ...S.btn('#eff6ff','#2563eb'), fontSize: 12, marginTop: 6 }}
                      onClick={() => { setEditGeomV(geo.coordinates[0].slice(0,-1).map(([lng,lat]) => [lat,lng])); setEditGeomMode(true); }}>
                      ✏️ Edit Boundary
                    </button>
                    ) : <p style={{ fontSize: 12, color: '#64748b' }}>Multipart boundaries and holes are preserved from the source dataset.</p>}
                  </div>
                );
              }
              return (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 7, padding: '10px 14px', marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>📍 No boundary drawn.</span>
                  {selectedPolygon.geometryType === 'lake' && (
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>GIS lake outlines can be imported when available.</span>
                  )}
                  <button type="button" style={{ ...S.btn('#eff6ff','#2563eb'), fontSize: 12, marginLeft: 12 }}
                    onClick={() => { setEditGeomV([]); setEditGeomMode(true); }}>
                    + Draw Boundary
                  </button>
                </div>
              );
            })()}

            {/* Species assignments */}
            <h3 style={{ fontSize: 15, margin: '16px 0 10px' }}>Species Assignments ({assignments.length})</h3>
            {assignments.length === 0 && <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>No species attached yet.</p>}

            {assignments.map((a) => {
              const speciesName = a.speciesId?.commonName || 'Unknown';
              const label = a.citizenLabel || scoreToLabel(a.score);
              return (
                <div key={a._id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: 8, background: '#fafafa' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{speciesName}</span>
                      <span style={S.scoreChip(a.score)}>{a.score > 0 ? `+${a.score}` : a.score}</span>
                      <span style={S.badge(a.status)}>{a.status}</span>
                      <div style={{ fontSize: 13, fontWeight: 600, color: a.score < 0 ? '#dc2626' : a.score > 0 ? '#16a34a' : '#64748b', marginTop: 4 }}>{label}</div>
                      {a.summary && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{a.summary}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {a.status === 'draft' && <button style={S.btn()} onClick={() => handleApproveAssign(a._id)}>✓ Publish</button>}
                      <button style={S.btn('#eff6ff','#2563eb')} onClick={() => startEdit(a)}>Edit</button>
                      <button style={S.btn('#fef2f2','#dc2626')} onClick={() => handleArchiveAssign(a._id)}>Archive</button>
                    </div>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 12, padding: 0 }}
                      onClick={() => setShowEvidence(showEvidence === a._id ? null : a._id)}>
                      {showEvidence === a._id ? '▲ Hide evidence' : '▼ View / add evidence'}
                    </button>
                    {showEvidence === a._id && <EvidencePanel tenantId={tenantId} assignmentId={a._id} onUpdate={() => loadAssignments(selectedPolygon._id)} />}
                  </div>
                </div>
              );
            })}

            {/* Attach / edit species form */}
            <div style={{ marginTop: 16, background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#1e40af' }}>{editAssignId ? 'Edit Assignment' : 'Attach Species'}</h3>

              {!editAssignId && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: '#475569', display: 'block', marginBottom: 4 }}>Species</label>
                  <SpeciesAutocomplete
                    canCreate={canBulkApprove}
                    value={assignForm.speciesName}
                    onSelect={(sel) => setAssignForm((p) => ({ ...p, speciesId: sel.speciesId || '', speciesName: sel.speciesName || '' }))}
                    popularSpecies={[]}
                    placeholder="Search species…"
                  />
                </div>
              )}
              {editAssignId && <p style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>Editing: <strong>{assignForm.speciesName}</strong></p>}

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#475569', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>Score</span>
                  <strong style={{ color: assignForm.score > 0 ? '#16a34a' : assignForm.score < 0 ? '#dc2626' : '#64748b' }}>
                    {assignForm.score > 0 ? `+${assignForm.score}` : assignForm.score} — {scoreToLabel(assignForm.score)}
                  </strong>
                </label>
                <input type="range" min={-10} max={10} step={1} value={assignForm.score}
                  onChange={(e) => setAssignForm((p) => ({ ...p, score: parseInt(e.target.value,10) }))}
                  style={{ width: '100%', accentColor: assignForm.score > 0 ? '#16a34a' : '#dc2626' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  <span>-10 (Avoid)</span><span>0</span><span>+10 (Focus)</span>
                </div>
              </div>

              <textarea placeholder="Brief summary for citizens (shown in map popup)…" value={assignForm.summary}
                onChange={(e) => setAssignForm((p) => ({ ...p, summary: e.target.value }))} rows={2} style={{ ...S.input, resize: 'vertical' }} />
              <textarea placeholder="Internal reason / evidence notes (staff only)…" value={assignForm.reason}
                onChange={(e) => setAssignForm((p) => ({ ...p, reason: e.target.value }))} rows={2} style={{ ...S.input, resize: 'vertical' }} />
              <select value={assignForm.season} onChange={(e) => setAssignForm((p) => ({ ...p, season: e.target.value }))} style={S.input}>
                <option value="">All seasons</option>
                <option value="spring">Spring</option>
                <option value="summer">Summer</option>
                <option value="fall">Fall</option>
                <option value="winter">Winter</option>
              </select>

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btn()} onClick={handleSaveAssign} disabled={savingAssign}>
                  {savingAssign ? 'Saving…' : editAssignId ? 'Update' : 'Attach Species'}
                </button>
                {editAssignId && (
                  <button style={S.btn('#f3f4f6','#374151')} onClick={() => { setEditAssignId(null); setAssignForm({ speciesId:'',speciesName:'',score:0,summary:'',reason:'',season:'' }); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
